import { type FC, useContext, useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { LoadingSpinner } from "widgets";

import { type JobResult, type Organism } from "../../graphql.js";
import { type IGenomeBrowserState } from "../../redux/modules/genome-browser.js";
import { type IAppState } from "../../redux/reducer.js";
import { ModalControlledOverlay } from "../core/overlays/index.js";
import { DndActiveContext } from "./GenomeViewerInteractiveSurface.js";
import { GenomeViewerToolbar } from "./GenomeViewerToolbar.js";
import { GenomeViewerTracksList } from "./GenomeViewerTracksList.js";
import BedTrackCustomizationDialog from "./bed-track/BedsTrackCustomizationDialog.js";
import NgsTrackCustomizationDialog from "./ngs-track/NgsTrackCustomizationDialog.js";
import VariantsTrackCustomizationDialog from "./variants-track/configuration/VariantsTrackCustomizationDialog.js";
import CoordinatesTrackCustomizationDialog from "@/ui-modules/genome-viewer/coordinate-track/CoordinatesTrackCustomizationDialog.tsx";

interface IGenomeViewerProps {
  // genome browser gets most of its parameters through the browser state.
  //   it is set by a call to initGenomeBrowser() in the parent component.
  jobResult?: JobResult;
  genomes?: Organism[]; // to show only the list of mutant genomes in the genome picker e.g. for Variant's genome browser
  startupEmpty?: boolean;
  selectionTooltipText?: string;
  showToolbars: boolean;
}

/**
 * The genome browser component is the top level smart component for the genome view.
 *
 * The component initiates the loading of the genome including all of its contigs and wires change handlers for
 * the currently visible contig and range with the Redux store. The UI of the browser consists of four main sub
 * components:
 *
 * - The browser heading, which shows the name of the genome, contig selector and zoom slider
 * - The graphical range selector, which shows the currently visible range on the scale of the whole contig
 * - The track list header acts as a separator between the graphical range selector and the tracks
 * - The track list shows the individual data tracks including another axis for the currently visible region
 *
 * created on 11/25/2016
 */
const GenomeViewer: FC<IGenomeViewerProps> = (props) => {
  const browser = useSelector<IAppState, IGenomeBrowserState>((state) => state.genomeBrowser, shallowEqual);
  const [showMutantGenomesSelectionDialogOfType, setShowMutantGenomesSelectionDialogOfType] = useState<string | null>(
    null,
  );
  const [showNgsTrackCustomizeDialog, setShowNgsTrackCustomizeDialog] = useState(false);
  const [showBedTrackCustomizeDialog, setShowBedTrackCustomizeDialog] = useState(false);
  const [showCoordinateTrackCustomizeDialog, setShowCoordinateTrackCustomizeDialog] = useState(false);
  const { setDndActive } = useContext(DndActiveContext);

  useEffect(() => {
    const preventEvent = (event: any /*WheelEvent<HTMLDivElement>*/) => {
      if (event.shiftKey || event.ctrlKey || event.metaKey) {
        event.stopPropagation();
        event.preventDefault();
      }
    };

    // prevent the browser behaviour with Ctrl+Mouse Wheel, only in this page
    window.addEventListener("mousewheel", preventEvent, { passive: false });
    window.addEventListener("DOMMouseScroll", preventEvent, { passive: false }); // for Firefox
    return () => {
      window.removeEventListener("mousewheel", preventEvent);
      window.removeEventListener("DOMMouseScroll", preventEvent);
    };
  }, []);

  // deactivate the dragging effect in the tracks, because it conflicts with the dragging in the customization dialogs
  useEffect(() => {
    setDndActive(
      !showNgsTrackCustomizeDialog &&
        !showBedTrackCustomizeDialog &&
        !showCoordinateTrackCustomizeDialog &&
        showMutantGenomesSelectionDialogOfType == null,
    );
  }, [
    setDndActive,
    showMutantGenomesSelectionDialogOfType,
    showNgsTrackCustomizeDialog,
    showBedTrackCustomizeDialog,
    showCoordinateTrackCustomizeDialog,
  ]);

  return (
    <div className="genome-browser-container">
      <div className="fill" style={{ overflow: "hidden" }}>
        <div className="genome-browser-panel" style={{ display: "flex", flexDirection: "column" }}>
          <div
            style={{
              display: props.showToolbars ? "flex" : "none",
              flexDirection: "column",
              background: "#fafafa",
              padding: 10,
              rowGap: 5,
              boxShadow: "inset 0 -1px #e6eaf2",
              flexBasis: "95px",
            }}
          >
            <GenomeViewerToolbar
              startupEmpty={props.startupEmpty}
              selectionTooltipText={props.selectionTooltipText}
              genomes={props.genomes}
              jobResult={props.jobResult}
            />
          </div>
          {browser.contig && !browser.isLoading ? (
            <GenomeViewerTracksList
              jobResult={props.jobResult}
              handleCustomizeVariantsTrack={setShowMutantGenomesSelectionDialogOfType}
              handleCustomizeNgsTrack={() => setShowNgsTrackCustomizeDialog(true)}
              handleCustomizeBedTrack={() => setShowBedTrackCustomizeDialog(true)}
              handleCustomizeCoordinateTrack={() => setShowCoordinateTrackCustomizeDialog(true)}
            />
          ) : (
            (!props.startupEmpty || browser.isLoading) && <LoadingSpinner inCenter />
          )}
        </div>
      </div>

      {showMutantGenomesSelectionDialogOfType && (
        <ModalControlledOverlay close={() => setShowMutantGenomesSelectionDialogOfType(null)} isOpen={true}>
          {({ close }) => (
            <VariantsTrackCustomizationDialog
              type={showMutantGenomesSelectionDialogOfType}
              onCancel={close}
              onApply={close}
            />
          )}
        </ModalControlledOverlay>
      )}

      {showNgsTrackCustomizeDialog && (
        <ModalControlledOverlay close={() => setShowNgsTrackCustomizeDialog(false)} isOpen={true}>
          {({ close }) => <NgsTrackCustomizationDialog onClose={close} />}
        </ModalControlledOverlay>
      )}
      {showBedTrackCustomizeDialog && (
        <ModalControlledOverlay close={() => setShowBedTrackCustomizeDialog(false)} isOpen={true}>
          {({ close }) => <BedTrackCustomizationDialog onClose={close} />}
        </ModalControlledOverlay>
      )}
      {showCoordinateTrackCustomizeDialog && (
        <ModalControlledOverlay close={() => setShowCoordinateTrackCustomizeDialog(false)} isOpen={true}>
          {({ close }) => <CoordinatesTrackCustomizationDialog onClose={close} />}
        </ModalControlledOverlay>
      )}
    </div>
  );
};

export { GenomeViewer };
